import * as THREE from 'three'
import Experience from '../experience.js'
import fireworkVertexShader from '../../shaders/firework/vertex.glsl'
import fireworkFragmentShader from '../../shaders/firework/fragment.glsl'
import gsap from 'gsap'

const fireworkAreaDist = 120;
const fireworkBaseHeight = 40;
const fireworkHeightRange = 10;
const baseBurstRadius = 10.0;
const burstSizeRange = 5.0;
const baseParticleSize = 1.2;
const particleSizeRange = 0.5;

export default class Fireworks {

    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene;
        this.textureLoader = this.experience.textureLoader;
        this.time = this.experience.time;
        this.sizes = this.experience.sizes;
        this.listener = this.experience.listener;
        this.audioLoader = this.experience.audioLoader;

        // Debug
        this.settings = {
            autoFireworks: true,
            singleDelayMax: 1000,
            clusterDelayMin: 100,
            clusterDelayMax: 600,
            pointLightIntensity: 2,
            pointLightDistance: 62,
            decay: 0.4,
            nbrPointLights: 10
        }
        const debugActions = {
            launchFirework: () => {
                this.createRandomFirework();
            }
        }
        this.debugFolder = this.experience.gui.addFolder('Fireworks')
        this.debugFolder.add(debugActions, 'launchFirework').name('Launch Firework');
        this.debugFolder.add(this.settings, 'autoFireworks').name('Auto fireworks');
        this.debugFolder.add(this.settings, 'singleDelayMax').min(100).max(4000).step(1).name('Firework Delay Max');
        this.debugFolder.add(this.settings, 'clusterDelayMin').min(100).max(4000).step(1).name('Cluster Delay Min');
        this.debugFolder.add(this.settings, 'clusterDelayMax').min(100).max(8000).step(1).name('Cluster Delay Max');
        this.debugFolder.add(this.settings, 'nbrPointLights').min(0).max(12).step(1).name('Point Lights')
            .onFinishChange((value) => this.setPointLights(value));
        this.debugFolder.add(this.settings, 'pointLightIntensity').min(0).max(100).step(0.001).name('Point Light Intensity')
        this.debugFolder.add(this.settings, 'pointLightDistance').min(0).max(100).step(0.001).name('Point Light Distance')
        this.debugFolder.add(this.settings, 'decay').min(0).max(4).step(0.001).name('Point Light Decay')
            .onChange((value) => {
                for (let i = 0; i < this.lights.length; i++) {
                    this.lights[i].decay = value;
                }
            });

        const soundFiles = [
            'fw_small_2',
            'fw_small_3',
            'fw_small_4',
            'fw_small_5',
            'fw_small_6',
            'fw_small_7',
            'fw_small_crackling',
            'fw_medium_2',
            'fw_medium_3',
            'fw_medium_4',
            'fw_medium_5',
            'fw_medium_6',
            'fw_medium_7',
            'fw_medium_crackling',
            'fw_medium_doubleclapafter',
            'fw_medium_punch',
            'fw_medium_punchy',
            'fw_large_1',
            'fw_large_2',
            'fw_large_3',
            'fw_large_4',
            'fw_large_5',
            'fw_large_loud',
            'fw_large_loud2'
        ];
        const ext = this.getAudioExt();

        // Create lists of sound files per each size category
        this.fwSoundsBySize = {};
        for (let i = 0; i < soundFiles.length; i++) {
            const path = './sounds/' + soundFiles[i] + ext;
            const audio = new THREE.Audio(this.listener);
            this.audioLoader.load(path, function(buffer) {
                audio.setBuffer(buffer);
            });
            //const audio = new Audio(fileName);
            const sz = soundFiles[i].split('_')[1];
            const arr = this.fwSoundsBySize[sz] || [];
            arr.push(audio);
            this.fwSoundsBySize[sz] = arr;
        }

        // Calculate sizeDividePoints from 0.0 to 1.0 based 
        // on how many sound files of each magnitude we have
        this.sizeDividePoints = [];
        this.sizeStr = [ 'small', 'medium', 'large' ];
        let prevMagnitude = 0;
        for (let i = 0; i < this.sizeStr.length - 1; i++) {
            const sz = this.sizeStr[i];
            prevMagnitude += this.fwSoundsBySize[sz].length / soundFiles.length;
            this.sizeDividePoints[i] = prevMagnitude;
        }

        this.setModel()
    }

    setModel() {
        // Textures
        this.textures = [
            this.textureLoader.load('./particles/1.png'),
            this.textureLoader.load('./particles/5.png'),
            this.textureLoader.load('./particles/6.png'),
        ];
        this.setPointLights(this.settings.nbrPointLights);
    }

    update() {
        // Shader does all the work
    }

    setPointLights(count) {
        this.curLightIdx = this.curLightIdx || 0;
        this.lights = this.lights || [];
        if (count > this.lights.length) {
            // Add more lights
            const start = this.lights.length;
            for (let i = start; i < count; i++) {
                const light = new THREE.PointLight(0xffffff, 0, this.settings.pointLightDistance, this.settings.decay);
                this.lights.push(light);
                this.scene.add(light);
            }
        }
        else if (count < this.lights.length) {
            // Remove excess lights
            for (let i = this.lights.length - 1; i >= count; i--) {
                gsap.killTweensOf(this.lights[i]);
                this.scene.remove(this.lights[i]);
                this.lights[i].dispose();
                this.lights.pop();
            }
            if (this.curLightIdx >= count) {
                this.curLightIdx = 0;
            }
        }
    }

    getAudioExt() {
        let ext = '.wav';
        const audioTest = document.createElement('audio');
        if (audioTest.canPlayType && '' != audioTest.canPlayType('audio/ogg; codecs=\"vorbis\"')) {
            ext = '.ogg';
        }
        return ext;
    }


    getRandomPosition(radiusRnd) {
        // Only launch fireworks in front of the camera
        const angle = ( 0.5 * Math.PI) - this.experience.controls.getAzimuthalAngle();
        const offsetX = Math.cos(angle) * fireworkAreaDist * 0.5;
        const offsetZ = Math.sin(angle) * fireworkAreaDist * 0.5;
        return new THREE.Vector3(
            - offsetX + ((Math.random() - 0.5) * fireworkAreaDist),
            fireworkBaseHeight + (fireworkHeightRange * radiusRnd), // bigger fireworks are higher
            - offsetZ + ((Math.random() - 0.5) * fireworkAreaDist),
        );
    }
    
    createRandomFirework() {
        const radiusRnd = Math.random();
        const size = baseParticleSize + Math.random() * particleSizeRange;
        const count = Math.round(300 + Math.random() * 100 * (1.0 + size));
        const texture = this.textures[Math.floor(this.textures.length * Math.random())];
        const color = new THREE.Color(); //'#595eff'
        const hslRand = Math.random();
        color.setHSL(hslRand, 1, 0.55);
    
        this.createFirework(
            count,
            this.getRandomPosition(radiusRnd),
            size,
            texture,
            radiusRnd,
            color
        );
    }

    playFireworkSound(radiusRnd) {
        if (!this.experience.settings.sound) {
            return;
        }
    
        // Find the appropriate sound group for this size of firework
        let sz = this.sizeStr[0];
        for (let i = 0; i < this.sizeDividePoints.length; i++) {
            if (radiusRnd <= this.sizeDividePoints[i]) break;
            sz = this.sizeStr[i+1];
        }
        
        // Play a random bang from that size group
        const idx = Math.floor(Math.random() * this.fwSoundsBySize[sz].length);
        const randBang = this.fwSoundsBySize[sz][idx];
        if (randBang.isPlaying) {
            randBang.stop();
        }
        randBang.offset = 0;
        randBang.play();
    }

    createFirework(count, position, size, texture, radiusRnd, color) {
        if (document.visibilityState === 'hidden') {
            return; // Prevent firework
        }

        // Geometry
        const positionsArray = new Float32Array(count * 3);
        const sizesArray = new Float32Array(count);
        const timeMultipliersArray =  new Float32Array(count);
        const burstRadius = radiusRnd * burstSizeRange + baseBurstRadius
    
        for (let i = 0; i < count; i++) {
            const i3 = i * 3;
    
            const spherical = new THREE.Spherical(
                burstRadius,
                Math.random() * Math.PI,
                Math.random() * Math.PI * 2
            );
            const position = new THREE.Vector3();
            position.setFromSpherical(spherical);
    
            positionsArray[i3    ] = position.x;
            positionsArray[i3 + 1] = position.y;
            positionsArray[i3 + 2] = position.z;
            sizesArray[i] = 2.5 + (0.3 * Math.random());
            timeMultipliersArray[i] = 1 + Math.random();
        }
    
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positionsArray, 3));
        geometry.setAttribute('aSize', new THREE.Float32BufferAttribute(sizesArray, 1));
        geometry.setAttribute('aTimeMultiplier', new THREE.Float32BufferAttribute(timeMultipliersArray, 1));
    
        texture.flipY = false;
        const material = new THREE.ShaderMaterial({
            vertexShader: fireworkVertexShader,
            fragmentShader: fireworkFragmentShader,
            uniforms: {
                uSize: new THREE.Uniform(size),
                uResolution: new THREE.Uniform(this.sizes.resolution),
                uTexture: new THREE.Uniform(texture),
                uColor: new THREE.Uniform(color),
                uProgress: new THREE.Uniform(0)
            },
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        })
    
        const firework = new THREE.Points(geometry, material);
        firework.position.copy(position);
        this.scene.add(firework);
        this.fireworkPointLight(position, radiusRnd, color);
        this.playFireworkSound(radiusRnd);
    
        const destroy = () => {
            this.scene.remove(firework);
            geometry.dispose();
            material.dispose();
        };
    
        gsap.to(
            material.uniforms.uProgress,
            { value: 1, duration: 3, ease: 'linear', onComplete: destroy },
        );
    }

    fireworkPointLight(position, radiusRnd, color) {
        const light = this.lights[this.curLightIdx];
        if (!light || light.intensity > 0) {
            return; // All lights currently in use
        }
        this.curLightIdx = (this.curLightIdx + 1) % this.lights.length;
        light.position.copy(position);
        light.color = color;
        light.distance = this.settings.pointLightDistance + (radiusRnd * 25.0);
        light.intensity = 0;
        const tl = gsap.timeline({});
        tl.to(light,
            {
                intensity: this.settings.pointLightIntensity,
                duration: 1.5,
                ease: 'power4.out',
            }
        );
        tl.add(() => {}, "+=.01")
        tl.to(light,
            {
                intensity: 0,
                duration: 1.5,
                ease: 'linear',
                onComplete: () => light.intensity = 0
            }
        );
    }

    start() {
        if (!this.clusterTimeoutId) {
            this.loopCluster();
        }
    }

    stop() {
        if (this.clusterTimeoutId) {
            window.clearTimeout(this.clusterTimeoutId);
            this.clusterTimeoutId = null;
        }
    }

    loopCluster() {
        let maxSingle = 0;

        if (this.settings.autoFireworks) {
            maxSingle = this.cluster();
        }

        const nextCluster = maxSingle
            + this.settings.clusterDelayMin
            + (Math.random() * (this.settings.clusterDelayMax - this.settings.clusterDelayMin));

        this.clusterTimeoutId = window.setTimeout(() => { this.loopCluster() }, nextCluster);
    }

    cluster() {
        const count = 1 + Math.floor(Math.random() * 4);
        let maxSingle = 0;

        for (let i = 0; i < count; i++) {
            const nextSingle = (Math.random() * this.settings.singleDelayMax);
            window.setTimeout(() => { this.createRandomFirework() }, nextSingle);
            maxSingle = Math.max(maxSingle, nextSingle);
        }
        
        return maxSingle;
    }
}